import { useCallback, useContext, useRef } from 'react'
import { config } from '../../config'
import { Context } from '../../context'
import { Moises as MoisesNew } from '../cli'
import { trigger } from '../events'
import { DerivedSegmentationParams } from './creation-params'

type UseAddOperation<TParams, TCallback> = (
  trackId: string,
  params: TParams | null
) => TCallback

type AddLyricsFn = (params: { language?: string }) => Promise<void>

type AddLyricsParams = {
  vocalsPath: string
}

type AddSegmentationFn = () => Promise<void>

type UseAddLyrics = UseAddOperation<AddLyricsParams, AddLyricsFn>

type UseAddSegmentation = UseAddOperation<
  DerivedSegmentationParams,
  AddSegmentationFn
>

type UseAddBeatschords = UseAddOperation<null, AddBeatschordsFn>

type AddBeatschordsFn = () => Promise<void>

const MoisesCLI = MoisesNew({ apiEndpoint: config.api.endpoint })

const useAddOperation = <
  TParams,
  TCallback extends (otherParams: TOtherParams) => Promise<void>,
  TOtherParams
>(
  name: string,
  trackId: string,
  params: TParams | null
): TCallback => {
  const called = useRef(false)
  const { userToken } = useContext(Context)

  const callback = useCallback(
    async (otherParams: TOtherParams) => {
      // Prevent multiple calls and don't count when there aren't params
      if (called.current || !params) {
        return
      }

      called.current = true

      MoisesCLI.auth(userToken)
      await MoisesCLI.addOperationsToTask({
        trackId,
        operations: [{ name, params: { ...params, ...otherParams } }]
      })

      await new Promise((resolve) => setTimeout(resolve, 2000))

      trigger('revalidate:tasks')
    },
    [userToken, trackId, name, params]
  )

  return callback as TCallback
}

export const useAddLyrics: UseAddLyrics = (...args) =>
  useAddOperation('LYRICS_B', ...args)

export const useAddSegmentation: UseAddSegmentation = (...args) =>
  useAddOperation('SEGMENTATION_C', ...args)

export const useAddBeatschords: UseAddBeatschords = (...args) =>
  useAddOperation('BEATSCHORDS_A', ...args)
